<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-bar-chart"></i> Báo cáo lỗ-lãi AI Token</h1>
        <p>So sánh doanh thu thu được từ khách vs chi phí thực OpenAI/DeepSeek. Tách chat vs QA.</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item"><router-link to="/report/ai-token-pnl">AI Token P&amp;L</router-link></li>
      </ul>
    </div>

    <!-- Filter -->
    <div class="row mb-3">
      <div class="col-md-3">
        <label>Kỳ (YYYY-MM)</label>
        <input type="month" v-model="period" class="form-control" @change="load"/>
      </div>
      <div class="col-md-3">
        <label>Client (tuỳ chọn)</label>
        <select v-model="clientFilter" class="form-control" @change="load">
          <option :value="null">— Tất cả —</option>
          <option v-for="c in clientList" :key="c._id" :value="c._id">{{ c.name }}</option>
        </select>
      </div>
      <div class="col-md-3 d-flex align-items-end">
        <button class="btn btn-success" @click="exportExcel" :disabled="loading || !rows.length">
          <i class="fa fa-file-excel-o"></i> Xuất Excel
        </button>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="row mb-3" v-if="summary">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-muted small">TỔNG TOKEN</div>
          <div class="h4 mb-0">{{ summary.totalTokens.toLocaleString('vi-VN') }}</div>
          <div class="small text-muted">
            Chat {{ Math.round(summary.totalChatTokens / Math.max(1, summary.totalTokens) * 100) }}% ·
            QA {{ Math.round(summary.totalQaTokens / Math.max(1, summary.totalTokens) * 100) }}%
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3 border-info">
          <div class="text-muted small">DOANH THU</div>
          <div class="h4 mb-0 text-info">{{ summary.revenueVnd.toLocaleString('vi-VN') }}đ</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3 border-warning">
          <div class="text-muted small">CHI PHÍ THỰC</div>
          <div class="h4 mb-0 text-warning">{{ summary.costVnd.toLocaleString('vi-VN') }}đ</div>
          <div class="small text-muted">Provider hiện tại: <b>{{ pricingRef && pricingRef.provider }}</b></div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3" :class="marginBorderClass">
          <div class="text-muted small">LÃI / LỖ</div>
          <div class="h4 mb-0" :class="marginTextClass">
            {{ summary.marginVnd.toLocaleString('vi-VN') }}đ
          </div>
          <div class="small" :class="marginTextClass">
            Margin {{ summary.marginPct }}%
          </div>
        </div>
      </div>
    </div>

    <!-- Bảng per client -->
    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center text-muted py-3">
          <i class="fa fa-spinner fa-spin"></i> Đang tải...
        </div>
        <table v-else class="table table-sm table-hover">
          <thead>
            <tr>
              <th>Client</th>
              <th>Gói</th>
              <th class="text-right">Token chat</th>
              <th class="text-right">Token QA</th>
              <th class="text-right">Tổng token</th>
              <th class="text-right">Doanh thu</th>
              <th class="text-right">Chi phí thực</th>
              <th class="text-right">Lãi / lỗ</th>
              <th class="text-right">Margin</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.clientId" :class="r.marginVnd < 0 ? 'table-danger' : ''">
              <td>
                <router-link :to="'/client/' + r.clientId">{{ r.clientName }}</router-link>
                <div v-if="r.domain" class="text-muted small">{{ r.domain }}</div>
              </td>
              <td>
                <span v-if="r.package" class="badge badge-info">{{ r.package }}</span>
                <span v-else class="text-muted small">—</span>
              </td>
              <td class="text-right">{{ r.chatTokens.toLocaleString('vi-VN') }}</td>
              <td class="text-right">{{ r.qaTokens.toLocaleString('vi-VN') }}</td>
              <td class="text-right"><b>{{ r.totalTokens.toLocaleString('vi-VN') }}</b></td>
              <td class="text-right">{{ r.revenueVnd.toLocaleString('vi-VN') }}đ</td>
              <td class="text-right text-warning">{{ r.costVnd.toLocaleString('vi-VN') }}đ</td>
              <td class="text-right" :class="r.marginVnd >= 0 ? 'text-success' : 'text-danger'">
                <b>{{ r.marginVnd.toLocaleString('vi-VN') }}đ</b>
              </td>
              <td class="text-right" :class="r.marginPct >= 50 ? 'text-success' : r.marginPct >= 20 ? 'text-info' : r.marginPct >= 0 ? 'text-warning' : 'text-danger'">
                {{ r.marginPct }}%
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="9" class="text-center text-muted py-3">Không có dữ liệu cho kỳ này.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
import moment from 'moment-timezone';

export default {
  name: 'AiTokenPnl',
  data() {
    return {
      loading: false,
      period: moment().format('YYYY-MM'),
      clientFilter: null,
      clientList: [],
      summary: null,
      rows: [],
      pricingRef: null,
    };
  },
  computed: {
    marginBorderClass() {
      const p = (this.summary && this.summary.marginPct) || 0;
      if (p < 0) return 'border-danger';
      if (p < 20) return 'border-warning';
      return 'border-success';
    },
    marginTextClass() {
      const p = (this.summary && this.summary.marginPct) || 0;
      if (p < 0) return 'text-danger';
      if (p < 20) return 'text-warning';
      return 'text-success';
    },
  },
  mounted() {
    this.loadClientList();
    this.load();
  },
  methods: {
    async loadClientList() {
      try {
        const r = await this.$service.get('/client', { params: { limit: 1000, isChatbot: true } });
        this.clientList = (r.data && (r.data.docs || r.data)) || [];
      } catch (e) { this.clientList = []; }
    },
    async load() {
      this.loading = true;
      try {
        const r = await this.$service.get('/ai-agent/pnl', { params: { period: this.period, clientId: this.clientFilter || undefined } });
        this.summary = r.data.summary;
        this.rows = r.data.rows;
        this.pricingRef = r.data.pricingRef;
      } catch (e) {
        this.summary = null; this.rows = [];
      } finally { this.loading = false; }
    },
    exportExcel() {
      // Build CSV đơn giản (admin có thể mở Excel) — không cần lib xlsx
      const rows = [
        ['Client', 'Domain', 'Gói', 'Token chat', 'Token QA', 'Tổng token', 'Doanh thu (đ)', 'Chi phí thực (đ)', 'Lãi/lỗ (đ)', 'Margin %'],
        ...this.rows.map(r => [
          r.clientName, r.domain, r.package || '',
          r.chatTokens, r.qaTokens, r.totalTokens,
          r.revenueVnd, r.costVnd, r.marginVnd, r.marginPct,
        ]),
      ];
      const csv = rows.map(row => row.map(v => {
        const s = String(v == null ? '' : v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')).join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-token-pnl-${this.period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
